import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';

const {
  FiUpload,
  FiFile,
  FiCheckCircle,
  FiAlertCircle,
  FiX,
  FiDownload,
  FiFileText,
  FiDatabase,
  FiUsers,
  FiTrendingUp,
  FiRefreshCw,
  FiInfo
} = FiIcons;

const ImportCSV = ({ onClose, onDataImported, recordType = 'providers' }) => {
  const [file, setFile] = useState(null);
  const [parsedData, setParsedData] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [importStatus, setImportStatus] = useState(null);
  const [errors, setErrors] = useState([]);
  const [warnings, setWarnings] = useState([]);
  const [previewData, setPreviewData] = useState([]);
  const [stats, setStats] = useState({ total: 0, valid: 0, invalid: 0, warnings: 0 });
  const fileInputRef = useRef(null);

  // Define required and optional fields for different record types
  const fieldRequirements = {
    providers: {
      required: ['name'], // Only name is truly required
      optional: ['phone', 'email', 'practice_name', 'practice_address', 'specialty', 'referral_count', 'birthday', 'last_referral_date', 'location', 'availability', 'bio'],
      defaults: {
        specialty: 'General Practitioner',
        availability: 'Available',
        referral_count: 0
      }
    },
    patients: {
      required: ['name'],
      optional: ['phone', 'email', 'date_of_birth', 'address', 'insurance'],
      defaults: {}
    },
    referrals: {
      required: ['patient_name', 'provider_name'],
      optional: ['date', 'status', 'notes', 'specialty'],
      defaults: {
        status: 'Pending',
        date: new Date().toISOString().split('T')[0]
      }
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setImportStatus(null);
      setErrors([]);
      setWarnings([]);
      parseFile(selectedFile);
    }
  };

  const parseFile = (file) => {
    if (file.size > 10 * 1024 * 1024) { // 10MB limit
      setErrors(['File size exceeds 10MB limit']);
      return;
    }

    setIsProcessing(true);
    const fileExtension = file.name.split('.').pop().toLowerCase();

    if (fileExtension === 'csv') {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        transformHeader: (header) => header.trim(),
        complete: (results) => {
          if (results.errors.length > 0) {
            const criticalErrors = results.errors.filter(e => e.type === 'Delimiter');
            if (criticalErrors.length > 0) {
              setErrors(criticalErrors.map(e => e.message));
            } else {
              setWarnings(results.errors.map(e => e.message));
            }
          }
          processData(results.data, Object.keys(results.data[0] || {}));
        },
        error: (error) => {
          setErrors([`CSV parsing error: ${error.message}`]);
          setIsProcessing(false);
        }
      });
    } else if (['xlsx', 'xls'].includes(fileExtension)) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
          const jsonData = XLSX.utils.sheet_to_json(firstSheet, { 
            header: 1,
            defval: '',
            blankrows: false
          });
          
          if (jsonData.length < 2) {
            setErrors(['File must contain at least a header row and one data row']);
            setIsProcessing(false);
            return;
          }

          // Convert to object format
          const headers = jsonData[0].map(h => String(h).trim());
          const dataRows = jsonData.slice(1).map(row => {
            const obj = {};
            headers.forEach((header, index) => {
              obj[header] = row[index] ? String(row[index]).trim() : '';
            });
            return obj;
          });

          processData(dataRows, headers);
        } catch (error) {
          setErrors([`Excel parsing error: ${error.message}`]);
          setIsProcessing(false);
        }
      };
      reader.readAsArrayBuffer(file);
    } else {
      setErrors(['Unsupported file format. Please upload CSV or XLSX files.']);
      setIsProcessing(false);
    }
  };

  const normalizeFieldName = (fieldName) => {
    return fieldName.toLowerCase()
      .replace(/[^a-z0-9]/g, '_')
      .replace(/_+/g, '_')
      .replace(/^_|_$/g, '');
  };

  const findFieldValue = (row, possibleNames) => {
    for (const name of possibleNames) {
      const normalizedName = normalizeFieldName(name);
      // Check exact match first
      if (row[name] !== undefined && row[name] !== '') {
        return row[name];
      }
      // Check normalized match
      const foundKey = Object.keys(row).find(key => 
        normalizeFieldName(key) === normalizedName
      );
      if (foundKey && row[foundKey] !== undefined && row[foundKey] !== '') {
        return row[foundKey];
      }
    }
    return '';
  };

  const processData = (data, detectedHeaders) => {
    setHeaders(detectedHeaders);
    setParsedData(data);
    setPreviewData(data.slice(0, 10));
    
    // Validate data with flexible requirements
    const validationResults = validateDataFlexible(data);
    setStats({
      total: data.length,
      valid: validationResults.valid,
      invalid: validationResults.invalid,
      warnings: validationResults.warnings
    });
    setErrors(validationResults.errors);
    setWarnings(validationResults.warningList);
    setIsProcessing(false);
  };

  const validateDataFlexible = (data) => {
    let valid = 0;
    let invalid = 0;
    let warnings = 0;
    const errorList = [];
    const warningList = [];
    
    const requirements = fieldRequirements[recordType] || fieldRequirements.providers;

    data.forEach((row, index) => {
      const rowErrors = [];
      const rowWarnings = [];
      
      // Check required fields with flexible matching
      requirements.required.forEach(requiredField => {
        const possibleNames = getPossibleFieldNames(requiredField);
        const value = findFieldValue(row, possibleNames);
        
        if (!value || !value.trim()) {
          rowErrors.push(`Missing required field: ${requiredField}`);
        }
      });

      // Check email format if provided (but don't require it)
      const emailValue = findFieldValue(row, ['email', 'Email', 'e-mail', 'E-mail', 'email_address']);
      if (emailValue && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailValue)) {
        rowWarnings.push('Invalid email format (will be skipped)');
      }

      // Check phone format if provided (but don't require it)
      const phoneValue = findFieldValue(row, ['phone', 'Phone', 'phone_number', 'Phone Number', 'telephone']);
      if (phoneValue && phoneValue.length > 0 && phoneValue.length < 7) {
        rowWarnings.push('Phone number seems too short');
      }

      // Add warnings for completely empty rows
      const hasAnyData = Object.values(row).some(value => value && value.toString().trim() !== '');
      if (!hasAnyData) {
        rowErrors.push('Empty row');
      }

      if (rowErrors.length > 0) {
        invalid++;
        if (errorList.length < 10) { // Show first 10 errors
          errorList.push({ row: index + 2, errors: rowErrors }); // +2 because of header row
        }
      } else {
        valid++;
        if (rowWarnings.length > 0) {
          warnings++;
          if (warningList.length < 10) {
            warningList.push({ row: index + 2, warnings: rowWarnings });
          }
        }
      }
    });

    return { valid, invalid, warnings, errors: errorList, warningList };
  };

  const getPossibleFieldNames = (fieldName) => {
    const fieldMappings = {
      name: ['Name', 'name', 'provider_name', 'Provider Name', 'full_name', 'Full Name', 'doctor_name', 'Doctor Name'],
      phone: ['Phone', 'phone', 'phone_number', 'Phone Number', 'telephone', 'Telephone', 'mobile', 'Mobile'],
      email: ['Email', 'email', 'e-mail', 'E-mail', 'email_address', 'Email Address'],
      practice_name: ['Practice Name', 'practice_name', 'clinic_name', 'Clinic Name', 'office_name', 'Office Name'],
      practice_address: ['Practice Address', 'practice_address', 'address', 'Address', 'clinic_address', 'office_address'],
      specialty: ['Specialty', 'specialty', 'specialization', 'Specialization', 'field', 'Field', 'department'],
      location: ['Location', 'location', 'city', 'City', 'state', 'State', 'region', 'Region'],
      referral_count: ['Referral Count', 'referral_count', 'referrals', 'Referrals', 'total_referrals'],
      birthday: ['Birthday', 'birthday', 'birth_date', 'Birth Date', 'date_of_birth', 'Date of Birth'],
      last_referral_date: ['Last Referral Date', 'last_referral_date', 'last_referral', 'most_recent_referral'],
      availability: ['Availability', 'availability', 'status', 'Status', 'available', 'Available']
    };
    
    return fieldMappings[fieldName] || [fieldName];
  };

  const handleImport = async () => {
    if (stats.valid === 0) {
      setErrors(['No valid records to import']);
      return;
    }

    setIsProcessing(true);
    
    try {
      const requirements = fieldRequirements[recordType] || fieldRequirements.providers;
      
      // Transform and clean the data
      const processedData = parsedData
        .map((row, index) => {
          // Check if row has required fields
          const hasRequiredFields = requirements.required.every(requiredField => {
            const possibleNames = getPossibleFieldNames(requiredField);
            const value = findFieldValue(row, possibleNames);
            return value && value.trim();
          });

          if (!hasRequiredFields) {
            return null; // Skip invalid rows
          }

          // Create normalized object with flexible field mapping
          const normalizedRow = {};
          
          // Map all possible fields
          Object.keys(requirements.defaults).forEach(field => {
            normalizedRow[field] = requirements.defaults[field];
          });

          // Map required fields
          requirements.required.forEach(field => {
            const possibleNames = getPossibleFieldNames(field);
            const value = findFieldValue(row, possibleNames);
            if (value) {
              normalizedRow[field] = value.trim();
            }
          });

          // Map optional fields
          requirements.optional.forEach(field => {
            const possibleNames = getPossibleFieldNames(field);
            const value = findFieldValue(row, possibleNames);
            if (value && value.trim()) {
              if (field === 'email') {
                // Validate email format
                if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
                  normalizedRow[field] = value.trim();
                }
              } else if (field === 'referral_count') {
                // Convert to number
                const numValue = parseInt(value) || 0;
                normalizedRow[field] = numValue;
              } else {
                normalizedRow[field] = value.trim();
              }
            }
          });

          return normalizedRow;
        })
        .filter(row => row !== null); // Remove invalid rows

      await onDataImported(processedData);
      
      setImportStatus({
        success: true,
        message: `Successfully processed ${processedData.length} ${recordType}`,
        imported: processedData.length,
        failed: stats.invalid,
        warnings: stats.warnings
      });
    } catch (error) {
      setImportStatus({
        success: false,
        message: `Import failed: ${error.message}`,
        imported: 0,
        failed: stats.total
      });
    }
    
    setIsProcessing(false);
  };

  const handleReset = () => {
    setFile(null);
    setParsedData([]);
    setHeaders([]);
    setPreviewData([]);
    setImportStatus(null);
    setErrors([]);
    setWarnings([]);
    setStats({ total: 0, valid: 0, invalid: 0, warnings: 0 });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const downloadTemplate = () => {
    const templates = {
      providers: [
        ['Name', 'Phone', 'Email', 'Practice Name', 'Practice Address', 'Specialty', 'Referral Count', 'Birthday', 'Last Referral Date', 'Availability'],
        ['Dr. John Doe', '(555) 123-4567', 'john@example.com', 'Doe Dental', '123 Main St, City, State, 12345', 'General Dentist', '50', '1980-01-15', '2024-01-15', 'Available'],
        ['Dr. Jane Smith', '', 'jane@example.com', 'Smith Orthodontics', '', 'Orthodontist', '', '', '', ''],
        ['Dr. Bob Wilson', '(555) 987-6543', '', '', '456 Oak Ave, City, State', '', '25', '', '2024-02-10', 'Busy']
      ],
      patients: [
        ['Name', 'Phone', 'Email', 'Date of Birth', 'Address', 'Insurance'],
        ['John Patient', '(555) 111-2222', 'patient@example.com', '1990-05-15', '789 Patient St', 'Blue Cross']
      ],
      referrals: [
        ['Patient Name', 'Provider Name', 'Date', 'Status', 'Notes', 'Specialty'],
        ['John Patient', 'Dr. Jane Smith', '2024-01-15', 'Pending', 'Routine checkup needed', 'General Dentist']
      ]
    };

    const templateData = templates[recordType] || templates.providers;
    const worksheet = XLSX.utils.aoa_to_sheet(templateData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Template');
    XLSX.writeFile(workbook, `${recordType}_template.xlsx`);
  };

  const requirements = fieldRequirements[recordType] || fieldRequirements.providers;

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl shadow-lg">
            <SafeIcon icon={FiUpload} className="h-6 w-6 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-800 dark:text-white">
              Import {recordType.charAt(0).toUpperCase() + recordType.slice(1)}
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mt-1">Upload CSV or Excel files to bulk import data</p>
            <p className="text-sm text-blue-600 dark:text-blue-400 mt-1">
              ✓ Only <strong>{requirements.required.join(', ')}</strong> {requirements.required.length === 1 ? 'is' : 'are'} required - other fields are optional
            </p>
          </div>
        </div>
        <button
          onClick={onClose}
          className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
        >
          <SafeIcon icon={FiX} className="h-6 w-6 text-gray-500" />
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-5 gap-4 mb-6">
        <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 dark:text-gray-400 text-sm">Total</p>
              <p className="text-2xl font-bold text-gray-800 dark:text-white">{stats.total}</p>
            </div>
            <SafeIcon icon={FiDatabase} className="h-8 w-8 text-blue-500" />
          </div>
        </div>

        <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 dark:text-gray-400 text-sm">Valid</p>
              <p className="text-2xl font-bold text-green-600">{stats.valid}</p>
            </div>
            <SafeIcon icon={FiCheckCircle} className="h-8 w-8 text-green-500" />
          </div>
        </div>

        <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 dark:text-gray-400 text-sm">Invalid</p>
              <p className="text-2xl font-bold text-red-600">{stats.invalid}</p>
            </div>
            <SafeIcon icon={FiAlertCircle} className="h-8 w-8 text-red-500" />
          </div>
        </div>

        <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 dark:text-gray-400 text-sm">Warnings</p>
              <p className="text-2xl font-bold text-yellow-600">{stats.warnings}</p>
            </div>
            <SafeIcon icon={FiInfo} className="h-8 w-8 text-yellow-500" />
          </div>
        </div>

        <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 dark:text-gray-400 text-sm">Success Rate</p>
              <p className="text-2xl font-bold text-purple-600">
                {stats.total > 0 ? Math.round((stats.valid / stats.total) * 100) : 0}%
              </p>
            </div>
            <SafeIcon icon={FiTrendingUp} className="h-8 w-8 text-purple-500" />
          </div>
        </div>
      </div>

      {/* File Upload Area */}
      <div className="bg-gray-50 dark:bg-gray-700 rounded-2xl p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-white">Upload File</h3>
          <button
            onClick={downloadTemplate}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors text-sm"
          >
            <SafeIcon icon={FiDownload} />
            <span>Download Template</span>
          </button>
        </div>

        <div
          onClick={() => fileInputRef.current?.click()}
          className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl p-8 text-center cursor-pointer hover:border-blue-400 hover:bg-gray-100 dark:hover:bg-gray-600 transition-all"
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.xlsx,.xls"
            onChange={handleFileChange}
            className="hidden"
          />
          <SafeIcon icon={FiUpload} className="h-12 w-12 mx-auto mb-4 text-blue-500" />
          <p className="text-lg font-semibold text-gray-800 dark:text-white mb-2">
            {file ? file.name : 'Click to upload or drag and drop'}
          </p>
          <p className="text-sm text-gray-600 dark:text-gray-400">CSV, XLSX, or XLS files (Max 10MB)</p>
          <p className="text-xs text-green-600 dark:text-green-400 mt-2">
            💡 Flexible import: Missing optional fields will be filled with defaults
          </p>
        </div>

        {file && (
          <div className="mt-4 flex items-center justify-between bg-white dark:bg-gray-800 p-4 rounded-lg border">
            <div className="flex items-center gap-3">
              <SafeIcon icon={FiFile} className="h-8 w-8 text-blue-500" />
              <div>
                <p className="font-semibold text-gray-800 dark:text-white">{file.name}</p>
                <p className="text-sm text-gray-600 dark:text-gray-400">{(file.size / 1024).toFixed(2)} KB</p>
              </div>
            </div>
            <button
              onClick={handleReset}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <SafeIcon icon={FiX} className="h-5 w-5 text-gray-500" />
            </button>
          </div>
        )}
      </div>

      {/* Data Preview */}
      <AnimatePresence>
        {previewData.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="bg-gray-50 dark:bg-gray-700 rounded-2xl p-6 mb-6"
          >
            <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">
              Data Preview (First 10 rows)
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-300 dark:border-gray-600">
                    {headers.map((header, index) => (
                      <th key={index} className="text-left p-3 text-gray-700 dark:text-gray-300 font-semibold">
                        {header}
                        {requirements.required.some(req => 
                          getPossibleFieldNames(req).includes(header)
                        ) && (
                          <span className="text-red-500 ml-1">*</span>
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {previewData.map((row, rowIndex) => (
                    <tr key={rowIndex} className="border-b border-gray-200 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-600">
                      {headers.map((header, colIndex) => (
                        <td key={colIndex} className="p-3 text-gray-800 dark:text-gray-200">
                          {row[header] || (
                            <span className="text-gray-400 italic">empty</span>
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
              * Required fields | Empty optional fields will use default values
            </p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Warnings */}
      <AnimatePresence>
        {warnings.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-2xl p-6 mb-6"
          >
            <div className="flex items-center gap-3 mb-4">
              <SafeIcon icon={FiInfo} className="h-6 w-6 text-yellow-500" />
              <h3 className="text-lg font-bold text-yellow-700 dark:text-yellow-400">Import Warnings</h3>
            </div>
            <div className="space-y-2">
              {warnings.map((warning, index) => (
                <div key={index} className="text-sm text-yellow-600 dark:text-yellow-400">
                  {typeof warning === 'object' ? (
                    <div>
                      <span className="font-semibold">Row {warning.row}:</span> {warning.warnings.join(', ')}
                    </div>
                  ) : (
                    <div>{warning}</div>
                  )}
                </div>
              ))}
              {stats.warnings > 10 && (
                <p className="text-sm text-yellow-500 dark:text-yellow-400 mt-2">
                  ...and {stats.warnings - 10} more warnings
                </p>
              )}
            </div>
            <p className="text-xs text-yellow-600 dark:text-yellow-400 mt-3">
              ℹ️ Records with warnings will still be imported with corrections applied
            </p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Errors */}
      <AnimatePresence>
        {errors.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-2xl p-6 mb-6"
          >
            <div className="flex items-center gap-3 mb-4">
              <SafeIcon icon={FiAlertCircle} className="h-6 w-6 text-red-500" />
              <h3 className="text-lg font-bold text-red-700 dark:text-red-400">Validation Errors</h3>
            </div>
            <div className="space-y-2">
              {errors.map((error, index) => (
                <div key={index} className="text-sm text-red-600 dark:text-red-400">
                  {typeof error === 'object' ? (
                    <div>
                      <span className="font-semibold">Row {error.row}:</span> {error.errors.join(', ')}
                    </div>
                  ) : (
                    <div>{error}</div>
                  )}
                </div>
              ))}
              {stats.invalid > 10 && (
                <p className="text-sm text-red-500 dark:text-red-400 mt-2">
                  ...and {stats.invalid - 10} more errors
                </p>
              )}
            </div>
            <p className="text-xs text-red-600 dark:text-red-400 mt-3">
              ⚠️ Records with errors will be skipped during import
            </p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Import Status */}
      <AnimatePresence>
        {importStatus && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className={`${
              importStatus.success 
                ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800' 
                : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
            } border rounded-2xl p-6 mb-6`}
          >
            <div className="flex items-center gap-3">
              <SafeIcon
                icon={importStatus.success ? FiCheckCircle : FiAlertCircle}
                className={`h-6 w-6 ${
                  importStatus.success ? 'text-green-500' : 'text-red-500'
                }`}
              />
              <div>
                <h3 className={`text-lg font-bold ${
                  importStatus.success 
                    ? 'text-green-700 dark:text-green-400' 
                    : 'text-red-700 dark:text-red-400'
                }`}>
                  {importStatus.message}
                </h3>
                <p className={`text-sm mt-1 ${
                  importStatus.success 
                    ? 'text-green-600 dark:text-green-300' 
                    : 'text-red-600 dark:text-red-300'
                }`}>
                  Imported: {importStatus.imported} | Failed: {importStatus.failed}
                  {importStatus.warnings > 0 && ` | Warnings: ${importStatus.warnings}`}
                </p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Action Buttons */}
      {parsedData.length > 0 && !importStatus && (
        <div className="flex gap-4 justify-end">
          <button
            onClick={handleReset}
            className="flex items-center gap-2 bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-500 text-gray-800 dark:text-gray-200 px-6 py-3 rounded-lg transition-colors"
          >
            <SafeIcon icon={FiRefreshCw} />
            <span>Reset</span>
          </button>
          <button
            onClick={handleImport}
            disabled={isProcessing || stats.valid === 0}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg transition-colors shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isProcessing ? (
              <>
                <SafeIcon icon={FiRefreshCw} className="animate-spin" />
                <span>Processing...</span>
              </>
            ) : (
              <>
                <SafeIcon icon={FiCheckCircle} />
                <span>Import {stats.valid} Records</span>
                {stats.warnings > 0 && (
                  <span className="text-xs bg-yellow-500 text-white px-2 py-1 rounded-full ml-2">
                    {stats.warnings} warnings
                  </span>
                )}
              </>
            )}
          </button>
        </div>
      )}

      {importStatus && (
        <div className="flex justify-end mt-4">
          <button
            onClick={onClose}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg transition-colors"
          >
            Close
          </button>
        </div>
      )}
    </div>
  );
};

export default ImportCSV;